// Текст абзаца для правил (типографика, живая проверка): строка + карта «индекс символа -> позиция в документе».
// Код (метка code), ссылки (link), URL и пути маскируются символом MASK - правила их не трогают; строчные
// атомы (формула, ссылка, сноска) - символ OBJ.
import type { Node as PMNode } from '@tiptap/pm/model';
import type { Transaction } from '@tiptap/pm/state';

export const MASK = '';
export const OBJ = '￼';

export interface BlockText {
  text: string;
  /** map[i] - позиция символа i в документе; map[text.length] - позиция конца содержимого */
  map: number[];
  /** атомы по индексу: i -> узел */
  atoms: Map<number, PMNode>;
}

const URL_RE = /(?:https?|ftp):\/\/[^\s«»"]+|www\.[^\s«»"]+|[\w.+-]+@[\w-]+(?:\.[\w-]+)+|[A-Za-z]:\\[^\s«»"]*/g;

/** pos - позиция начала текстового блока (перед открывающим токеном). */
export function blockText(node: PMNode, pos: number): BlockText {
  let text = '';
  const map: number[] = [];
  const atoms = new Map<number, PMNode>();
  node.forEach((ch, off) => {
    const p = pos + 1 + off;
    if (ch.isText) {
      const code = ch.marks.some((m) => m.type.name === 'code');
      const link = ch.marks.some((m) => m.type.name === 'link');
      const t = ch.text || '';
      // знаки препинания в конце ссылки («http://ya.ru/;») - часть предложения, а не адреса
      const keep = link && !code ? t.length - (t.match(/[.,;:!?)»]+$/) || [''])[0].length : t.length;
      for (let i = 0; i < t.length; i++) { map.push(p + i); text += (code || link) && i < keep ? MASK : t[i]; }
    } else {
      atoms.set(text.length, ch);
      map.push(p);
      text += ch.type.name === 'hardBreak' ? '\n' : OBJ;
    }
  });
  map.push(pos + 1 + node.content.size);
  text = text.replace(URL_RE, (m) => {
    const core = m.replace(/[.,;:!?)»]+$/, '');          // «…/;» в конце позиции перечисления - знак, не адрес
    return MASK.repeat(core.length) + m.slice(core.length);
  });
  return { text, map, atoms };
}

/** URL, почта и пути строки -> MASK, как в blockText (для строк вне документа: сверка типографики с writer). */
export function maskUrls(text: string): string {
  return text.replace(URL_RE, (m) => {
    const core = m.replace(/[.,;:!?)»]+$/, '');
    return MASK.repeat(core.length) + m.slice(core.length);
  });
}

export interface Edit { from: number; to: number; insert: string }

/** Применить правки строки (индексы в BlockText) к транзакции - с конца, чтобы позиции не сдвигались. mapFrom -
 *  номер шага транзакции, на котором снят BlockText (по умолчанию - исходный документ транзакции). */
export function applyEdits(tr: Transaction, bt: BlockText, edits: Edit[], mapFrom = 0) {
  const sorted = [...edits].sort((a, b) => b.from - a.from);
  let lastFrom = Infinity;
  for (const e of sorted) {
    if (e.to > lastFrom) continue;                       // перекрывающиеся правки - пропускаем
    const mp = mapFrom ? tr.mapping.slice(mapFrom) : tr.mapping;
    const from = mp.map(bt.map[e.from]);
    const to = mp.map(bt.map[e.to] ?? bt.map[bt.map.length - 1]);
    if (e.insert) tr.insertText(e.insert, from, to); else tr.delete(from, to);
    lastFrom = e.from;
  }
}
